import React, {forwardRef, useContext, useMemo, useState} from "react";
import {UserHomepageControllerContext} from "../../../../../../index";
import {UserHomepageComponentTypes} from "../../../../../../models";
import {Collapse} from "reactstrap";
import UserHomepageControlPanelComponentDatesSection from "../dates";
import UserHomepageControlPanelComponentSearch from "../search";
import {ReactSortable} from "react-sortablejs";
import UserHomepageUtils from "../../../../../../core/utils";
import {deepEqual} from "../../../../../../../../../core/services/utils/utils";
import UserHomepageControlPanelContent from "../../../content";
import UserHomepageControlPanelComponentCreatePart from "../../../content/create/part";
import UserHomepageControlPanelComponentCreateCategory from "../../../content/create/category";
import UserHomepageControlPanelComponentCreateBanner from "../../../content/create/banner";
import useAccessLevels from "../../../../../../../../hooks/use-access-levels";
import Button from "../../../../../../../../components/base/button/button";
import CarouselImageDialog from "../../../../../../../../components/dialogs/carousel-image";

const UserHomepageControlPanelComponentContents = forwardRef(({
                                                                  tabId,
                                                                  component: updatedComponent,
                                                                  currentComponent,
                                                                  dragging: draggingComponent,
                                                                  updating,
                                                              }, datesSectionRef) => {
    const controller = useContext(UserHomepageControllerContext);
    const [dragging, setDragging] = useState(false);
    const [createImageDialogOpen, setCreateImageDialogOpen] = useState(false);
    const operatorAccessLevels = useAccessLevels();

    const contentList = useMemo(() =>
            UserHomepageUtils.getComponentContentList(updatedComponent),
        [updatedComponent?.content, updatedComponent?.type])

    const showSearchSection = useMemo(() => {
        return [
            UserHomepageComponentTypes.timeBased,
            UserHomepageComponentTypes.smallProduct,
            UserHomepageComponentTypes.mediumProduct,
            UserHomepageComponentTypes.largeProduct,
        ].includes(updatedComponent?.type?.id)
    }, [updatedComponent?.type])

    const showDates = useMemo(() => {
        return [
            UserHomepageComponentTypes.timeBased,
        ].includes(updatedComponent?.type?.id)
    }, [updatedComponent?.type])

    /**
     * Fires whenever the order of the component-content has changed.
     * @param {any[]} reorderedContentList
     */
    const reorderContent = async (reorderedContentList) => {
        const reorderedComponentIds = reorderedContentList.map(e => e.id);
        if (deepEqual(contentList.map(e => e.id), reorderedComponentIds)) {
            // if not changed, do nothing.
            return;
        }
        controller.reorderContent(
            tabId,
            updatedComponent.id,
            reorderedContentList
        )
    }

    /**
     * Adds a part to the parts of the component content.
     * @param {any} part
     */
    const addPart = (part) => {
        controller.addContent(
            tabId,
            updatedComponent.id,
            part,
            false,
        )
    }

    /**
     * Removes a banner to the banners of the component content.
     * @param {any} banner
     */
    const removeBanner = (banner) => {
        controller.removeContent(
            tabId,
            updatedComponent.id,
            banner.id,
            false,
        )
    }

    /**
     * Adds a banner to the banners of the component content.
     * @param {any} banner
     */
    const addBanner = (banner) => {
        controller.addContent(
            tabId,
            updatedComponent.id,
            banner,
            false,
        )
    }

    /**
     * Adds a category to the categories of the component content.
     * @param {any} category
     */
    const addCategory = (category) => {
        controller.addContent(
            tabId,
            updatedComponent.id,
            category,
            false,
        )
    }

    const addCarouselImage = ({image, imageWidth, imageHeight}) => {
        controller.addContent(
            tabId,
            updatedComponent.id,
            image,
            false,
        );
        controller.updateComponent(tabId, {
            id: updatedComponent.id,
            content: {
                ...(updatedComponent.content ?? {}),
                imageWidth,
                imageHeight,
            },
        }, false);
    };

    const createContent = useMemo(() => {
        switch (updatedComponent?.type?.id) {
            case UserHomepageComponentTypes.timeBased:
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.largeProduct:
                return <UserHomepageControlPanelComponentCreatePart
                    tabId={tabId}
                    componentId={updatedComponent?.id}
                    parts={contentList}
                    updating={updating}
                    add={addPart}
                />
            case UserHomepageComponentTypes.category:
                return <UserHomepageControlPanelComponentCreateCategory
                    categories={contentList}
                    updating={updating}
                    add={addCategory}
                    industryId={updatedComponent?.industryId}
                />;
            case UserHomepageComponentTypes.slideShow:
                return <UserHomepageControlPanelComponentCreateBanner
                    multiSelect
                    updating={updating}
                    existingBanners={contentList}
                    add={addBanner}
                    remove={removeBanner}
                />;
            case UserHomepageComponentTypes.banner:
                if (contentList.length >= 1)
                    return <></>; // we can only add a single banner.

                return <UserHomepageControlPanelComponentCreateBanner
                    updating={updating}
                    existingBanners={contentList}
                    add={addBanner}
                    remove={removeBanner}
                />
            case UserHomepageComponentTypes.imageCarousel:
                return (
                    <>
                        <div className={'mx-2 d-flex align-items-center justify-content-center'}>
                            <Button
                                disabled={updating}
                                variant={'contained'}
                                color={'primary'}
                                onClick={() => setCreateImageDialogOpen(true)}
                            >
                                Add Image
                            </Button>
                        </div>
                        <CarouselImageDialog
                            open={createImageDialogOpen}
                            close={() => setCreateImageDialogOpen(false)}
                            mode={'create'}
                            existingImages={contentList}
                            imageWidth={updatedComponent?.content?.imageWidth}
                            imageHeight={updatedComponent?.content?.imageHeight}
                            onSuccess={addCarouselImage}
                        />
                    </>
                );
            default:
                return <></>;
        }
    }, [contentList, updatedComponent, tabId, updating, createImageDialogOpen])


    const dates = useMemo(() => (
        <Collapse isOpen={showDates}>
            <div>
                {
                    showDates &&
                    <UserHomepageControlPanelComponentDatesSection
                        componentId={updatedComponent.id}
                        type={updatedComponent.type}
                        currentEndDate={UserHomepageUtils.getTimeBasedFinishDateTime(updatedComponent)}
                        currentStartDate={updatedComponent.startDate}
                        tabId={tabId}
                        timeBased
                        ref={datesSectionRef}
                    />
                }
            </div>
        </Collapse>
    ), [showDates, tabId, updatedComponent, currentComponent, datesSectionRef])

    const searchSection = useMemo(() => (
        <Collapse isOpen={showSearchSection}>
            <div>
                {
                    showSearchSection &&
                    (
                        operatorAccessLevels.packages.lookup.nexusSearch
                    ) &&
                    <UserHomepageControlPanelComponentSearch
                        updatedComponent={updatedComponent}
                        updating={updating}
                        tabId={tabId}
                    />
                }
            </div>
        </Collapse>
    ), [updatedComponent, updating, showSearchSection, operatorAccessLevels.packages.lookup.nexusSearch, tabId]);

    return (
        <>
            {dates}
            <div>
                <ReactSortable
                    list={contentList}
                    setList={reorderContent}
                    className={'user-homepage-control-panel-contents'}
                    onStart={() => setDragging(true)}
                    onEnd={() => setDragging(false)}
                    handle={'.handle'}
                    group={'content'}
                    animation={210}
                    forceFallback={false}
                    swapThreshold={0.65}
                    ghostClass={"dragged-content"}
                >
                    {contentList.map(content => (
                        <UserHomepageControlPanelContent
                            key={content.id ?? content.fileGuid}
                            tabId={tabId}
                            componentId={updatedComponent.id}
                            componentType={updatedComponent.type}
                            content={content}
                            dragging={dragging || draggingComponent}
                            imageWidth={updatedComponent?.content?.imageWidth}
                            imageHeight={updatedComponent?.content?.imageHeight}
                        />
                    ))}
                </ReactSortable>
            </div>
            <div className={'mb-2'}>
                {createContent}
            </div>
            {searchSection}
        </>
    );
})

export default UserHomepageControlPanelComponentContents;
